import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  const openNotif = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.asset_id) navigate(`/asset/${n.asset_id}`);
    fetchNotifs();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); fetchNotifs(); } catch {}
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && fetchNotifs()}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center hover:border-white/30 transition"
        >
          <Bell size={16} className="text-zinc-300" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-mono bg-cyan8r text-black rounded-full"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0F0F0F] border-white/10 text-white w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-sm font-medium">Notifications</div>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-cyan8r hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-zinc-500">You're all caught up.</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openNotif(n)}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-0 transition hover:bg-white/5 ${!n.read ? "bg-white/[0.02]" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan8r flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{n.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{n.message}</div>
                    <div className="text-[10px] font-mono text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
